import { gql } from "@apollo/client"
import { DateTime } from "luxon"
import styles from "../../../styles/Participant-Employment.module.css"
import { BoardType, TopicStatus, TagType, Ascending } from "../Types/Enums"
import { getStatusText } from "../Types/TypeUtils"

export function showError(toast, mge, errors) {
    if (errors) {
        console.log(`${mge}: ${JSON.stringify(errors)}`)
    }
    toast.current.show({
        severity: "error",
        detail: mge,
        life: 3000,
    })
}

export const Actions = {
    Add: 0,
    Edit: 1,
    View: 2
}

export const DateTimeFormat = "MM/dd/yyyy h:mm a"
export const DateFormat = "MM/dd/yyyy"
export const TimeFormat = "hh:mm a"

export const convertToLocalDateTime = (date) =>
    DateTime.fromISO(date, { zone: "utc" }).toLocal().toJSDate()
export const formatToLocalDateTime = (date, format) =>
    DateTime.fromISO(date, { zone: "utc" }).toLocal().toFormat(format)

export const getActualDate = () => {
    return DateTime.utc().toISO()
}

export const formatDate = (date) => {
    //Asure that date has time 00:00:00
    const formattedDate = DateTime.fromJSDate(date).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
    })
    return formattedDate.toJSDate()
}

export const requiredFieldIsMissing = (field) => {
    if (field === null || field === undefined) return true
    if (
        (typeof field === "string" || field instanceof String) &&
        field.trim() === ""
    )
        return true
    return false
}

export const isMostRecent = (date1, date2) => {
    // Check if both dates are null
    if (!date1 && !date2) {
        return null
    }

    // Check if one of the dates is null
    if (!date1) {
        return false
    }
    if (!date2) {
        return true
    }

    // Ensure both dates are interpreted as UTC
    const d1 = new Date(date1.endsWith('Z') ? date1 : date1 + 'Z')
    const d2 = new Date(date2.endsWith('Z') ? date2 : date2 + 'Z')

    // Compare the dates
    return (d1 > d2) ? true : false
}

export const isDateInRange = (dateToCompare, dateRange) => {
    let startDate = DateTime.fromJSDate(dateRange[0])
    let endDate = dateRange[1] ? DateTime.fromJSDate(dateRange[1]) : null
    let date = DateTime.fromISO(dateToCompare)

    if (dateRange.length === 1 || endDate === null) {
        return date.hasSame(startDate, 'day')
    } else {
        return date >= startDate && date <= endDate
    }
}

export const renderRequireFieldErrorMessage = (field, mge) => {
    if (requiredFieldIsMissing(field))
        return <small className="p-error">{mge}</small>
    return null
}

export const staffNameInitials = (name) => {
    return name.first.charAt(0).toUpperCase() + name.last.charAt(0).toUpperCase()
}


export const capitalizeFirstLetter = (string, html) => {
    if (html) {
        string = "" + string
        return string.replace(/(<\w+>)*(\w).*/, (match, p1, p2) => {
            return match.replace(p1 + p2, p1 + p2.toUpperCase())
        })
    } else {
        return string && string.length > 1
            ? string.charAt(0).toUpperCase() + string.slice(1)
            : string
    }
}

export const renderTableDataCell = (item) => {
    if (item.type === "date") {
        return (
            <td key={item.label.replaceAll(" ", "_").toLowerCase()}>
                {item.value != null
                    ? formatToLocalDateTime(item.value, DateFormat)
                    : ""}
            </td>
        )
    }
    if (item.type === "datetime") {
        return (
            <td key={item.label.replaceAll(" ", "_").toLowerCase()}>
                {item.value != null
                    ? formatToLocalDateTime(item.value, DateTimeFormat)
                    : ""}
            </td>
        )
    }
    if (item.type === "html") {
        return (
            <td
                className={styles.html_cell}
                key={item.label.replaceAll(" ", "_").toLowerCase()}
                dangerouslySetInnerHTML={{
                    __html:
                        item.value != null ? capitalizeFirstLetter(item.value, true) : "",
                }}
            />
        )
    }
    return (
        <td key={item.label.replaceAll(" ", "_").toLowerCase()}>
            {item.value != null ? capitalizeFirstLetter(item.value) : ""}
        </td>
    )
}

export const getEnterpriseOrganization = (organizations) => {
    if (organizations.length > 0) {
        organizations.filter((b) => b.type === BoardType.Enterprise)
    }
    return null
}

export const containsValue = (value, valueToMatch) => {
    if (typeof value === 'string' && value.toUpperCase().includes(valueToMatch.toUpperCase())) {
        return true
    }
    return false
}

export const renderCreatedColumn = (topic) => {
    return `${topic.createdByName.full_name} on ${formatToLocalDateTime(topic.createdAt, DateTimeFormat)}`
}

export const renderUpdatedActivity = (topic) => {
    let updatedActivityDate = ""
    let updatedActivityBy = ""

    const mostRecent = isMostRecent(topic.lastActivity, topic.lastUpdatedAt)

    if (mostRecent == null) {
        return null
    }
    if (mostRecent) {
        updatedActivityDate = topic.lastActivity
        updatedActivityBy = topic.lastActivityByName
    } else {
        updatedActivityDate = topic.lastUpdatedAt
        updatedActivityBy = topic.lastUpdatedBy
    }

    return {
        by: updatedActivityBy,
        on: updatedActivityDate
    }
}

export const renderTag = (tag) => {
    if (tag.type === TagType["Default Participant"]) {
        const metadata = JSON.parse(tag.metadata)
        return `${metadata.name} - PTP ID ${metadata.alias}`
    }
    return tag.name
}

const renderTagsAsString = (topic) => {
    return topic.tags.map(tag => renderTag(tag)).join(',')
}

const extractTextFromHtml = (htmlString) => {
    // Create a new temporary div element
    var tempDiv = document.createElement('div')

    // Set the HTML content to the div
    tempDiv.innerHTML = htmlString

    // Extract the text content from the div
    var text = tempDiv.textContent || tempDiv.innerText || ''

    return text
}

const extractTextFromComments = (comments) => {
    return comments.map(comment => comment.messageText).join(',')
}

export const mapTopicTableFields = (topic) => {
    const updatedActivity = renderUpdatedActivity(topic)
    return {
        "subject": capitalizeFirstLetter(topic.subject),
        "createdBy": topic.createdByName.full_name,
        "createdOn": formatToLocalDateTime(topic.createdAt, DateTimeFormat),
        "updatedActivityBy": updatedActivity ? updatedActivity.by.full_name : "",
        "updatedActivityOn": updatedActivity ? formatToLocalDateTime(updatedActivity.on, DateTimeFormat) : "",
        "tagsStr": renderTagsAsString(topic),
        "tags": topic.tags,
        "status": getStatusText(topic.status),
        "new": !topic.notOpened,
        "activity": !topic.newActivity,
        "description": extractTextFromHtml(topic.messageText),
        "comments": extractTextFromComments(topic.comments)
    }
}

export const sortFn = (a, b, propName, sortOrder) => {
    const fieldsA = mapTopicTableFields(a)
    const fieldsB = mapTopicTableFields(b)
    if (fieldsA[propName] < fieldsB[propName]) {
        return sortOrder === Ascending ? -1 : 1
    }
    if (fieldsA[propName] > fieldsB[propName]) {
        return sortOrder === Ascending ? 1 : -1
    }
    return 0
}

export const updateAndMoveTopic = (activeTopics, archivedTopics, topic) => {
    let foundIndex = activeTopics.findIndex(t => t.id === topic.id)

    let listToUpdate = 1

    if (foundIndex === -1) {
        foundIndex = archivedTopics.findIndex(t => t.id === topic.id)
        listToUpdate = 2
    }

    if (foundIndex !== -1) {
        if (listToUpdate === 1) {
            const actualData = activeTopics[foundIndex]
            topic.createdByName = actualData.createdByName
            topic.lastActivityBy = actualData.lastActivityBy
            topic.lastActivityByName = actualData.lastActivityByName

            if ([TopicStatus.Active, TopicStatus.Completed].includes(actualData.status)
                && topic.status === TopicStatus.Archived
            ) {
                // Should move topic to Archived list
                activeTopics.splice(foundIndex, 1)
                archivedTopics.push(topic)
            } else {
                activeTopics[foundIndex] = topic
            }
        } else {
            const actualData = archivedTopics[foundIndex]
            topic.createdByName = actualData.createdByName
            topic.lastActivityBy = actualData.lastActivityBy
            topic.lastActivityByName = actualData.lastActivityByName

            if (actualData.status === TopicStatus.Archived
                && [TopicStatus.Active, TopicStatus.Completed].includes(topic.status)
            ) {
                // Should move topic to Active list
                archivedTopics.splice(foundIndex, 1)
                activeTopics.push(topic)
            } else {
                archivedTopics[foundIndex] = topic
            }
        }
    }
}

export const findName = (id, list) => {
    const staff = list.find((staff) => staff.staff_id === id)
    return staff ? {
        first: staff.staff_name_first,
        last: staff.staff_name_last,
        full_name: staff.staff_full_name,
        id
    } : {
        first: "",
        last: "",
        full_name: "",
        id: ""
    }
}

export const getMentions = (htmlString, suggestions) => {
    // Create a temporary DOM element to parse the HTML
    let tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlString

    // Find all the mentions in the HTML
    const mentionElements = tempDiv.getElementsByClassName('mention')

    // Create an object to store the mentions and ensure there are no duplicates
    let mentions = {}

    // Iterate over each mention and extract the required data
    for (let i = 0; i < mentionElements.length; i++) {
        const id = mentionElements[i].getAttribute('data-id')
        const suggestion = suggestions.find((s) => s.id === id && s.choosable)
        if (suggestion) {
            const mention = {
                staffId: suggestion.id,
                organizationId: suggestion.organizationId
            }
            if (!(mention.staffId in mentions)) {
                mentions[mention.staffId] = mention
            }
        } else {
            console.log(`Mention: could not find user with StaffId: ${id}`)
        }
    }

    return Object.values(mentions)
}

export const GET_STAFF_NAMES = gql`
  query getStaffNamesByIds($staff_id: ID!, $organization_id: ID!, $staff_ids: [ID!]!) {
    getStaffNamesByIds(staff_id:$staff_id, organization_id:$organization_id, staff_ids: $staff_ids) {
        data
    }
  }
`

export const GET_ORGANIZATIONS = gql`
query getOrganizations($staff_id: ID!, $enterprise_id: ID!) {
    organizations: organizationsByEnterpriseUid(
        staff_id: $staff_id,  
        enterprise_id: $enterprise_id
        ) {
            id: organization_id
            name: organization_name
        }
}
`

export const GET_ORGANIZATIONS_AND_REFERRALS = gql`
query getOrganizations($staff_id: ID!, $enterprise_id: ID!, $service_staff_id: ID!, $partner_enterprise_id: ID!  ) {
    organizations: organizationsByEnterpriseUid(
        staff_id: $staff_id,  
        enterprise_id: $enterprise_id
        ) {
            id: organization_id
            name: organization_name
        },
    referralOrganizations: organizationsByEnterpriseUid(
        staff_id: $service_staff_id
        enterprise_id: $partner_enterprise_id
      ) {
        id: organization_id
        name: organization_name
      }
}
`

export const GET_ORGANIZATION = gql`
query getOrganization(
    $staff_id: ID!
    $organization_id: ID!
  ) {
    organization(
      staff_id: $staff_id
      organization_id: $organization_id
    ) {
        id: organization_id
        name: organization_name
    }
  }
`

export const GET_PARTICIPANTS_BY_ENTERPRISE_ID = gql`
query participantsByEnterpriseUid(
    $staff_id: ID!
    $staff_organization_id: ID!
    $enterprise_id: ID!
  ) {
    participantsByEnterpriseUid(
        staff_id: $staff_id
        staff_organization_id: $staff_organization_id
        enterprise_id: $enterprise_id
      ) {
        organization_id
        organization_name
        participant_id
        participant_alias
        firstName: participant_name_first
        lastName: participant_name_last
    }
  }
`

export const GET_PARTICIPANTS_BY_ORG_LIST = gql`
query participantsByOrgList(
    $staff_id: ID!
    $staff_organization_id: ID!
    $organizations_ids: [ID!]
  ) {
    participantsByOrgList(
        staff_id: $staff_id
        staff_organization_id: $staff_organization_id
        organizations_ids: $organizations_ids
      ) {
        organization_id
        organization_name
        participant_id
        participant_alias
        firstName: participant_name_first
        lastName: participant_name_last
    }
  }
`

export const GET_USERS_BY_ENTERPRISE_ID = gql`
  query allUsersByEnterpriseId($staff_id: ID!, $staff_organization_id: ID!, $enterprise_id: ID!) {
    allUsersByEnterpriseId(staff_id: $staff_id, staff_organization_id: $staff_organization_id, enterprise_id: $enterprise_id) {
        organization_id
        organization_alias
        organization_name
        staff_role
        staff_role_name
        staff_id
        staff_alias
        staff_name_first
        staff_name_last
        staff_full_name
    }
  }
`

export const GET_USERS_BY_ORG_LIST = gql`
  query allUsersByOrgList($staff_id: ID!, $staff_organization_id: ID!, $organizations_ids: [ID!]) {
    allUsersByOrgList(staff_id: $staff_id, staff_organization_id: $staff_organization_id, organizations_ids: $organizations_ids) {
        organization_id
        organization_alias
        organization_name
        staff_role
        staff_role_name
        staff_id
        staff_alias
        staff_name_first
        staff_name_last
        staff_full_name
    }
  }
`